import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { IDispenseRecordSchema } from '../../../../@types/dispense';

const FinalSummary = ({ finalData, dense = false }: any) => {
  const { getValues } = useFormContext();
  const [openModal, setOpenModal] = useState(false);
  // Previous examinations are not fetched here yet. This used to be seeded with
  // two invented rows ("Eye Checking" 19/03/2024, "Nhs-Eyetest" 20/03/2024),
  // which showed fabricated history next to real clinical data for every patient.
  const [previousRecords] = useState<IDispenseRecordSchema[]>([]);

  const rightFinalSPH = getValues('final.distanceRightSPH');
  const rightFinalCYL = getValues('final.distanceRightCYL');
  const rightFinalAxis = getValues('final.distanceRightAxis');
  const nearRightAddFinal = getValues('final.nearRightAdd');
  const intermediateRightFinal = getValues('final.intermediateRightAdd');
  const distanceRightPrismFinal = getValues('final.distanceRightPrism');

  const leftFinalSPH = getValues('final.distanceLeftSPH');
  const leftFinalCYL = getValues('final.distanceLeftCYL');
  const leftFinalAxis = getValues('final.distanceLeftAxis');
  const nearLeftAddFinal = getValues('final.nearLeftAdd');
  const intermediateLeftFinal = getValues('final.intermediateLeftAdd');
  const distanceLeftPrismFinal = getValues('final.distanceLeftPrism');

  const isRightEmpty =
    !rightFinalSPH &&
    !rightFinalCYL &&
    !rightFinalAxis &&
    !nearRightAddFinal &&
    !intermediateRightFinal &&
    !distanceRightPrismFinal &&
    !finalData?.distanceRightSPH &&
    !finalData?.distanceRightCYL &&
    !finalData?.distanceRightAxis &&
    !finalData?.nearRightAdd &&
    !finalData?.intermediateRightAdd &&
    !finalData?.distanceRightPrism;

  const isLeftEmpty =
    !leftFinalSPH &&
    !leftFinalCYL &&
    !leftFinalAxis &&
    !nearLeftAddFinal &&
    !intermediateLeftFinal &&
    !distanceLeftPrismFinal &&
    !finalData?.distanceLeftSPH &&
    !finalData?.distanceLeftCYL &&
    !finalData?.distanceLeftAxis &&
    !finalData?.nearLeftAdd &&
    !finalData?.intermediateLeftAdd &&
    !finalData?.distanceLeftPrism;

  const handleOpenModalDispense = () => setOpenModal(!openModal);

  return (
    <Box>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openModal}
        onClose={handleOpenModalDispense}
      >
        <DialogContent sx={{ px: 0 }}>
          <Table>
            <TableBody>
              <TableRow
                sx={{
                  borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
                  backgroundColor: '#919EAB14',
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: '600',
                    fontSize: '18px',
                    textAlign: 'center',
                  }}
                >
                  Examination Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: '600',
                    fontSize: '18px',
                    textAlign: 'center',
                  }}
                >
                  Examination Type
                </TableCell>
              </TableRow>
              {previousRecords.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{ fontSize: '16px', textAlign: 'center' }}
                  >
                    No previous records
                  </TableCell>
                </TableRow>
              )}
              {previousRecords.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.2)' }}
                >
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '18px',
                      textAlign: 'center',
                    }}
                  >
                    {item.date.toLocaleDateString()}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '18px',
                      textAlign: 'center',
                    }}
                  >
                    {item.type}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          mb: dense ? 0 : 3,
          padding: 2,
          borderRadius: '15px',
          border: '1px solid #6373813b',
          position: 'relative',
        }}
      >
        <Typography sx={{ mb: 3, ml: 4 }} variant="body2">
          <strong>Right:</strong>
          {isRightEmpty ? (
            <Typography sx={{ display: 'inline', ml: 1 }} variant="body2">
              No records found
            </Typography>
          ) : (
            <Typography sx={{ display: 'inline', ml: 3 }}>
              {rightFinalSPH || finalData?.distanceRightSPH}/
              {rightFinalCYL || finalData?.distanceRightCYL} ×{' '}
              {rightFinalAxis || finalData?.distanceRightAxis}
              {nearRightAddFinal
                ? ` ADD + ${nearRightAddFinal}`
                : finalData?.nearRightAdd
                  ? ` ADD + ${finalData.nearRightAdd}`
                  : ''}
              {intermediateRightFinal || finalData?.intermediateRightAdd
                ? `, Inter ADD + ${
                    intermediateRightFinal || finalData?.intermediateRightAdd
                  }`
                : ''}
              {distanceRightPrismFinal || finalData?.distanceRightPrism
                ? ` ${distanceRightPrismFinal || finalData?.distanceRightPrism}`
                : ''}
            </Typography>
          )}
        </Typography>

        <Typography sx={{ ml: 4 }} variant="body2">
          <strong>Left:</strong>
          {isLeftEmpty ? (
            <Typography sx={{ display: 'inline', ml: 1 }} variant="body2">
              No records found
            </Typography>
          ) : (
            <Typography sx={{ display: 'inline', ml: 3 }}>
              {leftFinalSPH || finalData?.distanceLeftSPH}/
              {leftFinalCYL || finalData?.distanceLeftCYL} ×{' '}
              {leftFinalAxis || finalData?.distanceLeftAxis}
              {nearLeftAddFinal
                ? ` ADD + ${nearLeftAddFinal}`
                : finalData?.nearLeftAdd
                  ? ` ADD + ${finalData.nearLeftAdd}`
                  : ''}
              {intermediateLeftFinal || finalData?.intermediateLeftAdd
                ? `, Inter ADD + ${
                    intermediateLeftFinal || finalData?.intermediateLeftAdd
                  }`
                : ''}
              {distanceLeftPrismFinal || finalData?.distanceLeftPrism
                ? ` ${distanceLeftPrismFinal || finalData?.distanceLeftPrism}`
                : ''}
            </Typography>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default FinalSummary;

/** Per-eye emptiness for a raw `refraction.final` object (no form merge). */
export function finalDataEyeEmpty(finalData: Record<string, unknown> | null): {
  right: boolean;
  left: boolean;
} {
  if (!finalData) {
    return { right: true, left: true };
  }
  const f = finalData;
  const rightEmpty =
    !f.distanceRightSPH &&
    !f.distanceRightCYL &&
    !f.distanceRightAxis &&
    !f.nearRightAdd &&
    !f.intermediateRightAdd &&
    !f.distanceRightPrism;
  const leftEmpty =
    !f.distanceLeftSPH &&
    !f.distanceLeftCYL &&
    !f.distanceLeftAxis &&
    !f.nearLeftAdd &&
    !f.intermediateLeftAdd &&
    !f.distanceLeftPrism;
  return { right: rightEmpty, left: leftEmpty };
}

export function pickLatestFinalFromRecords(
  records: unknown,
): Record<string, unknown> | null {
  if (!Array.isArray(records)) return null;
  for (const rec of records) {
    const f = (rec as { refraction?: { final?: unknown } })?.refraction?.final;
    if (!f || typeof f !== 'object') continue;
    const { right, left } = finalDataEyeEmpty(f as Record<string, unknown>);
    if (!right || !left) {
      return f as Record<string, unknown>;
    }
  }
  return null;
}

type FinalSummaryReadOnlyProps = {
  finalData: Record<string, unknown> | null;
  loading?: boolean;
  showPlaceholder?: boolean;
};

export function FinalSummaryReadOnly({
  finalData,
  loading = false,
  showPlaceholder = false,
}: FinalSummaryReadOnlyProps) {
  const { right: isRightEmpty, left: isLeftEmpty } =
    finalDataEyeEmpty(finalData);
  const f = finalData || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'end', width: '100%', mb: 2 }}>
        <Typography variant="h6" sx={{ mr: 1 }}>
          Final
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          padding: 2,
          borderRadius: '15px',
          border: '1px solid #6373813b',
          position: 'relative',
          minHeight: loading ? 80 : undefined,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography sx={{ mb: 5, ml: 3 }}>
              Right:
              {isRightEmpty ? (
                <Typography sx={{ display: 'inline', ml: 3 }}>
                  {showPlaceholder
                    ? 'Select a patient to load records'
                    : 'No records found'}
                </Typography>
              ) : (
                <Typography sx={{ display: 'inline', ml: 3 }}>
                  {String(f.distanceRightSPH ?? '')}/
                  {String(f.distanceRightCYL ?? '')} ×{' '}
                  {String(f.distanceRightAxis ?? '')}
                  {f.nearRightAdd ? ` ADD + ${String(f.nearRightAdd)}` : ''}
                  {f.intermediateRightAdd
                    ? `, Inter ADD + ${String(f.intermediateRightAdd)}`
                    : ''}
                  {f.distanceRightPrism
                    ? ` ${String(f.distanceRightPrism)}`
                    : ''}
                </Typography>
              )}
            </Typography>

            <Typography sx={{ ml: 3 }}>
              Left:
              {isLeftEmpty ? (
                <Typography sx={{ display: 'inline', ml: 3 }}>
                  {showPlaceholder
                    ? 'Select a patient to load records'
                    : 'No records found'}
                </Typography>
              ) : (
                <Typography sx={{ display: 'inline', ml: 3 }}>
                  {String(f.distanceLeftSPH ?? '')}/
                  {String(f.distanceLeftCYL ?? '')} ×{' '}
                  {String(f.distanceLeftAxis ?? '')}
                  {f.nearLeftAdd ? ` ADD + ${String(f.nearLeftAdd)}` : ''}
                  {f.intermediateLeftAdd
                    ? `, Inter ADD + ${String(f.intermediateLeftAdd)}`
                    : ''}
                  {f.distanceLeftPrism ? ` ${String(f.distanceLeftPrism)}` : ''}
                </Typography>
              )}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
